import { CSSProperties, useContext, useEffect, useState } from "react";
import { GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import numberFormat from "../../../../../../utils/Utils";
import { cardStyle, sectionTitleStyle } from "../cardStyles";
import { Invoice, InvoiceStatus } from "./types";

interface InvoiceHistoryProps {
  invoices: Invoice[];
  onDownload?: (inv: Invoice) => void;
  onPay?: (inv: Invoice) => void;
}

const actionButtonStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "#4f46e5",
  fontSize: 13,
  fontWeight: 600,
  padding: 0,
};

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr);
  if (!dateStr || Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Tailwind no está en el bundle de esta app (las directivas de App.scss no se
// importan), así que el gating responsive no puede hacerse con clases md:*.
const DESKTOP_QUERY = "(min-width: 768px)";

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(DESKTOP_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
};

const InvoiceHistory = ({ invoices, onDownload, onPay }: InvoiceHistoryProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);
  const isDesktop = useIsDesktop();

  const statusConfig: Record<
    InvoiceStatus,
    { label: string; bg: string; color: string }
  > = {
    paid: {
      label: translate("newDashboard.billing.paid"),
      bg: "#ecfdf5",
      color: "#065f46",
    },
    pending: {
      label: translate("newDashboard.billing.pending"),
      bg: "#fffbeb",
      color: "#92400e",
    },
    failed: {
      label: translate("newDashboard.billing.failed"),
      bg: "#fef2f2",
      color: "#991b1b",
    },
  };

  const renderAction = (inv: Invoice) => {
    if (inv.status === "paid" && inv.downloadUrl) {
      return (
        <button
          onClick={() =>
            onDownload ? onDownload(inv) : window.open(inv.downloadUrl, "_blank")
          }
          style={actionButtonStyle}
        >
          <GlobalIcon iconName="DownloadIcon" size="XS" color="#4f46e5" />
          {translate("newDashboard.billing.download")}
        </button>
      );
    }
    if (inv.payable && onPay) {
      return (
        <button onClick={() => onPay(inv)} style={actionButtonStyle}>
          <GlobalIcon iconName="CurrencyEuroIcon" size="XS" color="#4f46e5" />
          {translate("newDashboard.billing.payNow")}
        </button>
      );
    }
    return null;
  };

  return (
    <div style={cardStyle}>
      <div style={sectionTitleStyle}>
        <GlobalIcon iconName="FileInvoiceIcon" size="XS" color="#4f46e5" />
        <h2 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.billing.history")}
        </h2>
      </div>

      {invoices.length === 0 ? (
        <p
          style={{
            fontSize: 14,
            color: "#9ca3af",
            fontStyle: "italic",
            textAlign: "center",
            padding: 32,
          }}
        >
          {translate("newDashboard.billing.noInvoices")}
        </p>
      ) : isDesktop ? (
        <div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
                  {["date", "concept", "amount", "status", "action"].map((col) => (
                    <th
                      key={col}
                      style={{
                        textAlign: col === "amount" ? "right" : "left",
                        padding: "10px 12px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#9ca3af",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                      }}
                    >
                      {translate(`newDashboard.billing.${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const st = statusConfig[inv.status];
                  return (
                    <tr key={inv.id} style={{ borderBottom: "1px solid #f9fafb" }}>
                      <td style={{ padding: "14px 12px", fontSize: 14, color: "#374151", fontWeight: 500 }}>
                        {formatDate(inv.date)}
                      </td>
                      <td style={{ padding: "14px 12px", fontSize: 14, color: "#6b7280" }}>
                        {inv.concept || translate("newDashboard.billing.monthlyPremium")}
                      </td>
                      <td style={{ padding: "14px 12px", fontSize: 14, fontWeight: 600, color: "#111827", textAlign: "right" }}>
                        {numberFormat(inv.amount, "price")}
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            fontSize: 12,
                            fontWeight: 600,
                            padding: "3px 10px",
                            borderRadius: 20,
                            backgroundColor: st.bg,
                            color: st.color,
                          }}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        {renderAction(inv) ?? (
                          <span style={{ fontSize: 13, color: "#d1d5db" }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {invoices.map((inv) => {
              const st = statusConfig[inv.status];
              return (
                <div
                  key={inv.id}
                  style={{
                    padding: 16,
                    backgroundColor: "#f9fafb",
                    borderRadius: 10,
                    border: "1px solid #f3f4f6",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}>
                      {formatDate(inv.date)}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        padding: "3px 10px",
                        borderRadius: 20,
                        backgroundColor: st.bg,
                        color: st.color,
                      }}
                    >
                      {st.label}
                    </span>
                  </div>
                  <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 4px" }}>
                    {inv.concept || translate("newDashboard.billing.monthlyPremium")}
                  </p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                      {numberFormat(inv.amount, "price")}
                    </span>
                    {renderAction(inv)}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default InvoiceHistory;
